import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { NodeShell } from './NodeShell';
import { ActionPill, SvgRemove, SvgEdit } from './ProductNode';

export const DESIGN_NODE_WIDTH = 260;

interface DesignNodeProps {
    nodeId: string;
    x: number;
    y: number;
    zoom: number;
    selected?: boolean;
    /** CDN URL of the rendered design — shown as the node's preview. */
    renderedUrl?: string;
    /** True while a Save is rasterizing + uploading. */
    saving?: boolean;
    /** Whether the user has configured anything yet (= fabricJson exists). */
    hasContent: boolean;
    onOpenEditor: () => void;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onRemove?: () => void;
    /** Trigger a browser download of the last-rendered design PNG. */
    onDownload?: () => void;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
    /**
     * Thumbnail URLs of every source image feeding this design (primary
     * + extras from a multi-select Create-image action). When 2+ urls
     * are provided AND no rendered output exists yet, the node renders
     * a 2×2 mosaic so vendors can see at a glance which images the
     * design will compose. Single-source designs ignore this and use
     * the existing empty-state ("Open editor"). After Save, the
     * rendered PNG always wins.
     */
    sourceThumbnailUrls?: string[];
}

/**
 * Canvas node for the Design editor (Fabric.js-based layered editor).
 * Displays the most recent Save'd rendering as a thumbnail; clicking
 * opens the DesignEditorModal where the vendor composes text / logo /
 * shape layers on top of the source image.
 */
export const DesignNode: React.FC<DesignNodeProps> = ({
    nodeId,
    x,
    y,
    zoom,
    selected,
    renderedUrl,
    saving,
    hasContent,
    onOpenEditor,
    onDrag,
    onClick,
    onRemove,
    onDownload,
    dimmed,
    sourceThumbnailUrls,
}) => {
    const { t } = useTranslation();
    // Multi-source designs render a 2×2 mosaic until the user saves a
    // rendering. The first 3 cells show concrete thumbnails; if there
    // are 5+ sources, the 4th cell collapses to a "+N more" overlay
    // instead of a thumbnail — matches Apple Photos / Notion gallery
    // patterns and keeps the load lean (max 4 images regardless of
    // selection size).
    const sources = sourceThumbnailUrls || [];
    const showMosaic = !renderedUrl && sources.length >= 2;
    const mosaicVisible = sources.length <= 4 ? sources : sources.slice(0, 3);
    const overflowCount = sources.length > 4 ? sources.length - 3 : 0;
    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={DESIGN_NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="#4A4945"
            dimmed={dimmed}
            label={t('CONTENT_STUDIO.NODE_DESIGN', 'Design')}
            onDrag={onDrag}
            onClick={onClick}
            pulse={saving}
            addHandle
            hoverAffordance={
                <div className="flex flex-col gap-1.5">
                    {onDownload && renderedUrl && (
                        <ActionPill
                            label={t('CONTENT_STUDIO.ACTION_DOWNLOAD', 'Download')}
                            icon={
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 4v12" />
                                    <path d="M6 12l6 6 6-6" />
                                    <path d="M4 20h16" />
                                </svg>
                            }
                            onClick={onDownload}
                        />
                    )}
                    {onRemove && (
                        <ActionPill
                            label={t('CONTENT_STUDIO.ACTION_REMOVE')}
                            icon={<SvgRemove />}
                            onClick={onRemove}
                            destructive
                        />
                    )}
                </div>
            }
        >
            <div className="flex flex-col">
                {/* Thumbnail preview area */}
                <div
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpenEditor(); }}
                    className="relative aspect-square w-full cursor-pointer overflow-hidden border-b bg-muted bg-cover bg-center"
                    style={{ backgroundImage: renderedUrl ? `url(${renderedUrl})` : undefined }}
                >
                    {!renderedUrl && !saving && !showMosaic && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                            <div className="size-7 text-muted-foreground">
                                <SvgEdit />
                            </div>
                            {t(
                                hasContent ? 'CONTENT_STUDIO.DESIGN_OPEN_EDITOR' : 'CONTENT_STUDIO.DESIGN_START',
                                hasContent ? 'Open editor' : 'Start designing',
                            )}
                        </div>
                    )}
                    {!renderedUrl && !saving && showMosaic && (
                        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[2px] bg-border">
                            {mosaicVisible.map((url, idx) => {
                                // Avoid leaving an empty cell at any
                                // count by spanning the layout based on
                                // total sources:
                                //   2 sources → each fills a full row
                                //   3 sources → first spans both cols
                                //               on top, others share
                                //               the bottom row
                                //   4+        → standard 2×2
                                let gridColumn: string | undefined;
                                let gridRow: string | undefined;
                                if (sources.length === 2) {
                                    gridRow = 'span 2';
                                } else if (sources.length === 3 && idx === 0) {
                                    gridColumn = 'span 2';
                                }
                                return (
                                    <div
                                        key={`${url}-${idx}`}
                                        className="relative bg-muted bg-cover bg-center"
                                        style={{ backgroundImage: `url(${url})`, gridColumn, gridRow }}
                                    />
                                );
                            })}
                            {overflowCount > 0 && (
                                <div className="relative flex items-center justify-center bg-foreground text-sm font-medium text-background">
                                    +{overflowCount}
                                </div>
                            )}
                        </div>
                    )}
                    {saving && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        </div>
                    )}
                </div>

                {/* Footer: open-editor button */}
                <button
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpenEditor(); }}
                    disabled={saving}
                    className="flex w-full items-center justify-center gap-2 bg-background py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span className="flex size-3.5 items-center">
                        <SvgEdit />
                    </span>
                    {hasContent
                        ? t('CONTENT_STUDIO.DESIGN_EDIT', 'Edit design')
                        : t('CONTENT_STUDIO.DESIGN_OPEN', 'Open editor')}
                </button>
            </div>
        </NodeShell>
    );
};
